import React, { useEffect, useRef, useState } from "react";
import "./TurnstileWidget.scss";

const SCRIPT_ID = "cf-turnstile-script";
const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstileScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();

  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    return new Promise((resolve, reject) => {
      if (window.turnstile) resolve();
      else {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", reject);
      }
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

/**
 * Cloudflare Turnstile (Managed + interaction-only).
 * Visible only when Cloudflare asks for a challenge; otherwise silent.
 * Increment resetKey to force a reset after submit.
 */
const TurnstileWidget = ({ onToken, resetKey = 0 }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const interactiveRef = useRef(false);
  onTokenRef.current = onToken;
  const [expanded, setExpanded] = useState(false);

  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey || !containerRef.current) return undefined;

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        if (widgetIdRef.current != null) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch (_) {
            /* ignore */
          }
          widgetIdRef.current = null;
        }

        interactiveRef.current = false;
        setExpanded(false);
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          appearance: "interaction-only",
          theme: "light",
          callback: (token) => {
            // Keep the Success banner only after a visible challenge.
            setExpanded(interactiveRef.current);
            onTokenRef.current?.(token);
          },
          "expired-callback": () => {
            interactiveRef.current = false;
            setExpanded(false);
            onTokenRef.current?.(null);
          },
          "error-callback": () => {
            interactiveRef.current = false;
            setExpanded(false);
            onTokenRef.current?.(null);
          },
          "before-interactive-callback": () => {
            interactiveRef.current = true;
            setExpanded(true);
          },
          "after-interactive-callback": () => {
            setExpanded(true);
          },
        });
      })
      .catch((err) => {
        console.error("Turnstile script failed to load:", err);
        onTokenRef.current?.(null);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  useEffect(() => {
    if (resetKey === 0) return;
    interactiveRef.current = false;
    setExpanded(false);
    if (widgetIdRef.current != null && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (_) {
        /* ignore */
      }
      onTokenRef.current?.(null);
    }
  }, [resetKey]);

  if (!siteKey) {
    return (
      <p className="field-err" style={{ marginTop: 8 }}>
        Security check unavailable (missing site key).
      </p>
    );
  }

  return (
    <div
      className={`mtk-turnstile${expanded ? " is-expanded" : ""}`}
      ref={containerRef}
      aria-hidden={!expanded}
    />
  );
};

export default TurnstileWidget;
